import { useMemo } from "react";
import {
  Box,
  Typography,
  IconButton,
  Divider,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Chip,
  Stack,
  Tooltip,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EvStationIcon from "@mui/icons-material/EvStation";
import Constant from "../../util/constants";
import { GetIconSrc } from "../../util/providerIcons";

const { connectorStatus, chargerStatus } = Constant;

function getStationStatus(loc) {
  if (!loc.devices || !loc.devices.length) return "unknown";
  const { stations, status } = loc.devices[0];
  if (!status || !chargerStatus.includes(status)) return "unknown";

  const isInUse = stations?.some(({ status: s }) =>
    [...connectorStatus.inUse, "IN USE", "IN_USE"].some(
      (x) => x.toUpperCase() === (s || "").toUpperCase()
    )
  );
  const isAvailable = stations?.some(({ status: s }) =>
    connectorStatus.available.some(
      (x) => x.toUpperCase() === (s || "").toUpperCase()
    )
  );

  if (status === "Active" && isInUse) return "in-use";
  if (status === "Active" && isAvailable) return "available";
  if (status === "Active") return "active";
  return "inactive";
}

function getStatusChip(status) {
  const map = {
    available: { label: "Available", color: "success" },
    "in-use": { label: "In Use", color: "warning" },
    active: { label: "Active", color: "info" },
    inactive: { label: "Inactive", color: "default" },
    unknown: { label: "Unknown", color: "default" },
  };
  const { label, color } = map[status] || map.unknown;
  return <Chip label={label} color={color} size="small" sx={{ fontSize: "0.65rem" }} />;
}

function getConnectorCount(loc) {
  if (!loc.devices) return 0;
  return loc.devices.reduce((count, device) => {
    return count + (device.stations?.length || 0);
  }, 0);
}

export default function ChargerListPanel({
  locations,
  networkFilters,
  onChargerClick,
  onClose,
}) {
  const siteLocations = useMemo(() => {
    if (!locations || !locations.length) return [];
    const siteGroups = {};
    locations.forEach((loc) => {
      const key =
        loc.siteId && loc.siteId.trim()
          ? `site_${loc.siteId.trim()}`
          : `latlng_${loc.latitude}_${loc.longitude}`;
      if (!siteGroups[key]) {
        siteGroups[key] = {
          ...loc,
          devices: [...(loc.devices || [])],
        };
      } else {
        const rep = siteGroups[key];
        if (loc.devices?.length) {
          rep.devices = [...rep.devices, ...loc.devices];
        }
      }
    });
    return Object.values(siteGroups);
  }, [locations]);

  return (
    <Paper
      elevation={8}
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: { xs: "100%", sm: 360 },
        height: "100%",
        zIndex: 1200,
        display: "flex",
        flexDirection: "column",
        borderRadius: { xs: 0, sm: "0 8px 8px 0" },
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.5,
          bgcolor: "var(--app-color, #1976d2)",
          color: "white",
          flexShrink: 0,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <EvStationIcon />
          <Typography variant="h6" fontSize="1rem" fontWeight={600}>
            Charging Stations
          </Typography>
          <Chip
            label={siteLocations.length}
            size="small"
            sx={{ bgcolor: "rgba(255,255,255,0.25)", color: "white", fontWeight: 700 }}
          />
        </Stack>
        <Tooltip title="Close list">
          <IconButton onClick={onClose} sx={{ color: "white" }} size="small">
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Divider />

      {/* List */}
      <Box sx={{ overflowY: "auto", flex: 1 }}>
        {siteLocations.length === 0 ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              No charging stations in the current map view.
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {siteLocations.map((loc, index) => {
              const status = getStationStatus(loc);
              const connectorCount = getConnectorCount(loc);
              const displayName = loc.display_name || loc.name || "Charging Station";

              let iconSrc;
              try {
                iconSrc = GetIconSrc({ location: loc, networkFilters, deviceAvailable: null });
              } catch {
                iconSrc = null;
              }

              return (
                <Box key={loc.party_id ? loc.id : loc._id || index}>
                  <ListItemButton
                    onClick={() => onChargerClick(loc)}
                    sx={{
                      px: 2,
                      py: 1.5,
                      "&:hover": { bgcolor: "action.hover" },
                    }}
                    alignItems="flex-start"
                  >
                    <ListItemAvatar sx={{ minWidth: 48 }}>
                      <Avatar
                        src={iconSrc || undefined}
                        sx={{
                          width: 40,
                          height: 40,
                          bgcolor: iconSrc ? "transparent" : "var(--app-color, #1976d2)",
                        }}
                      >
                        {!iconSrc && <EvStationIcon fontSize="small" />}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography
                          variant="subtitle2"
                          fontWeight={600}
                          textTransform="capitalize"
                          noWrap
                          title={displayName}
                        >
                          {displayName}
                        </Typography>
                      }
                      secondary={
                        <Stack spacing={0.5} mt={0.25}>
                          {loc.address && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                              }}
                            >
                              {loc.address}
                            </Typography>
                          )}
                          <Stack direction="row" spacing={1} alignItems="center" mt={0.5}>
                            {getStatusChip(status)}
                            {connectorCount > 0 && (
                              <Typography variant="caption" color="text.secondary">
                                {connectorCount} connector{connectorCount !== 1 ? "s" : ""}
                              </Typography>
                            )}
                          </Stack>
                        </Stack>
                      }
                    />
                  </ListItemButton>
                  {index < siteLocations.length - 1 && <Divider component="li" />}
                </Box>
              );
            })}
          </List>
        )}
      </Box>
    </Paper>
  );
}
